import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { Send, MapPin, Mail, Linkedin, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
// --- Constants & Types ---
const COLORS = {
  background: '#0D2B24',
  soilOverlay: 'rgba(10, 17, 17, 0.4)',
  formContainer: '#1F3A33',
  teal: '#2BC7B7',
  glowTeal: '#4DE7D4',
  error: '#A38E78',
  white: '#F3F5F1' // Mycelium White
};
type FormState = {
  name: string;
  email: string;
  message: string;
};
type FieldStatus = 'empty' | 'typing' | 'valid' | 'error';
// --- Helper Functions for Organic Paths ---
// Generate a random-ish but deterministic wiggle path
const generateWiggle = (startX: number, startY: number, length: number, seed: number, amplitude: number = 10) => {
  let d = `M ${startX} ${startY}`;
  const segments = Math.floor(length / 20);
  let currentY = startY;
  let currentX = startX;
  for (let i = 0; i < segments; i++) {
    const nextY = currentY + 20;
    // Pseudo-random offset based on seed and index
    const offset = Math.sin(seed + i) * amplitude;
    const nextX = startX + offset;
    // Control points for smooth bezier
    const cp1x = currentX;
    const cp1y = currentY + 10;
    const cp2x = nextX;
    const cp2y = nextY - 10;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${nextX} ${nextY}`;
    currentY = nextY;
    currentX = nextX;
  }
  // Final segment to exact length if needed, or just stop
  return d;
};
// Generate branches
const generateBranches = (parentPath: string, count: number, seed: number) => {
  // This is a simplified approach. In a real complex system we'd parse the SVG path.
  // Here we'll just spawn branches from estimated points along the vertical descent.
  // Assuming the parent path goes roughly down from startY.
  const branches = [];
  for (let i = 0; i < count; i++) {
    const verticalOffset = 30 + i * 40; // Spacing
    const side = i % 2 === 0 ? 1 : -1;
    const branchSeed = seed + i * 100;
    // Start point (rough estimate relative to parent flow)
    // We'll just make them start from the center line + wiggle offset
    const startX = Math.sin(branchSeed) * 10;
    const startY = verticalOffset;
    const length = 20 + Math.sin(branchSeed * 2) * 15; // Random length 20-35
    const endX = startX + length * side * 0.8;
    const endY = startY + length * 0.6;
    // Quadratic curve for branch
    const d = `M ${startX} ${startY} Q ${startX + side * 5} ${startY + length / 2}, ${endX} ${endY}`;
    branches.push({
      d,
      id: `branch-${seed}-${i}`,
      yOffset: verticalOffset
    });
  }
  return branches;
};
// --- Components ---
const FounderCard = () => <motion.div initial={{
  opacity: 0,
  x: 20
}} animate={{
  opacity: 1,
  x: 0
}} transition={{
  delay: 0.8
}} className="bg-[#1F3A33] rounded-2xl p-6 border border-[#2BC7B7]/20 shadow-lg max-w-xs w-full" data-id="element-382">
    <div className="flex items-center gap-4 mb-4" data-id="element-383">
      <div className="relative" data-id="element-384">
        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#2BC7B7] relative z-10" data-id="element-385">
          <img src="https://cdn.magicpatterns.com/uploads/nPrbWRd99BLA7hL4gLSpQo/7_Natalya.jpg" alt="Natalya Wallin" className="w-full h-full object-cover" data-id="element-386" />
        </div>
        {/* Ring pulse */}
        <motion.div className="absolute inset-0 rounded-full border border-[#2BC7B7]" animate={{
        scale: [1, 1.2, 1],
        opacity: [0.5, 0, 0.5]
      }} transition={{
        duration: 3,
        repeat: Infinity
      }} data-id="element-387" />
      </div>
      <div data-id="element-388">
        <h3 className="text-white font-semibold text-lg" data-id="element-389">Natalya Wallin</h3>
        <p className="text-[#2BC7B7] text-xs uppercase tracking-wider" data-id="element-390">
          Founding Partner & GP
        </p>
      </div>
    </div>
    <p className="text-[#F3F5F1]/70 text-sm italic border-l-2 border-[#2BC7B7]/30 pl-3" data-id="element-391">
      "I personally read every message. We're looking for the outliers, the
      resilient, the ones forged in nature."
    </p>
  </motion.div>;
const ContactCard = ({
  icon: Icon,
  title,
  value,
  href
}: any) => <a href={href} target={href ? '_blank' : undefined} rel={href ? 'noopener noreferrer' : undefined} className={`flex flex-col items-center p-6 bg-[#1F3A33] rounded-xl border border-[#2BC7B7]/30 hover:border-[#2BC7B7]/80 hover:bg-[#2BC7B7]/15 hover:shadow-[0_0_20px_rgba(43,199,183,0.2)] transition-all duration-300 group ${!href ? 'cursor-default' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]'}`} data-id="element-392">
    <div className="w-10 h-10 rounded-full bg-[#0D2B24] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform" data-id="element-393">
      <Icon className="w-6 h-6 text-[#2BC7B7] group-hover:text-[#4DE7D4] transition-colors" data-id="element-394" />
    </div>
    <span className="text-[#F3F5F1]/60 text-xs uppercase tracking-wider mb-1 font-medium" data-id="element-395">
      {title}
    </span>
    <span className="text-white font-medium text-center" data-id="element-396">{value}</span>
  </a>;
export function ContactRootSystem() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    message: ''
  });
  const [focused, setFocused] = useState<keyof FormState | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success'>('idle');
  // Validation Logic
  const isNameValid = form.name.length > 2;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const isMessageValid = form.message.length > 10;
  const allValid = isNameValid && isEmailValid && isMessageValid;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allValid) return;
    setStatus('sending');
    // Simulate network request and bloom animation time
    await new Promise(resolve => setTimeout(resolve, 3500));
    setStatus('success');
  };
  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      message: ''
    });
    setStatus('idle');
  };
  // Calculate root lengths based on input
  const nameRootLength = Math.min(form.name.length * 5, 150);
  const emailRootLength = Math.min(form.email.length * 4, 150);
  const messageRootLength = Math.min(form.message.length * 1.5, 220);
  return <div className="min-h-screen bg-[#0D2B24] relative overflow-hidden font-sans selection:bg-[#2BC7B7]/30" data-id="element-397">
      {/* Background Textures */}
      <div className="absolute inset-0 bg-[#0A1111] opacity-40 pointer-events-none" data-id="element-398" />
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
    }} data-id="element-399" />

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-400">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-401">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-id="element-402">
            LET'S GROW SOMETHING TOGETHER
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-[#2BC7B7] text-lg font-medium mb-6" data-id="element-403">
            Plant the seed of a conversation
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-[#F3F5F1]/70 leading-relaxed" data-id="element-404">
            If you are passionate about building a world of vitality as an
            entrepreneur, investor, or partner, we want to hear from you.
            <br data-id="element-405" />
            <br data-id="element-406" />
            Vitality Vida Partners, LLC
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-id="element-407">
          {/* Left Column: Form (Centerpiece) */}
          <div className="lg:col-span-8 lg:col-start-3 xl:col-span-6 xl:col-start-4 relative" data-id="element-408">
            {/* Form Container */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6
          }} className="bg-[#1F3A33] rounded-[24px] p-8 md:p-12 relative overflow-hidden shadow-2xl border border-[#2BC7B7]/20" style={{
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.5)'
          }} data-id="element-409">
              {/* Soil Texture Overlay inside form */}
              <div className="absolute inset-0 bg-[#0A1111] opacity-30 pointer-events-none mix-blend-multiply" data-id="element-410" />
              <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%232BC7B7' fill-opacity='0.4'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' /%3E%3C/g%3E%3C/svg%3E")`
            }} data-id="element-411" />

              {/* SUCCESS STATE OVERLAY */}
              <AnimatePresence data-id="element-412">
                {status === 'success' && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#0D2B24]/90 backdrop-blur-sm text-center p-8" data-id="element-413">
                    <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  duration: 1.5
                }} className="w-24 h-24 rounded-full bg-[#2BC7B7]/20 flex items-center justify-center mb-6 relative" data-id="element-414">
                      <div className="absolute inset-0 rounded-full border border-[#2BC7B7] animate-ping opacity-20" data-id="element-415" />
                      <Check className="w-10 h-10 text-[#2BC7B7]" data-id="element-416" />
                    </motion.div>
                    <motion.h2 initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.2
                }} className="text-3xl font-bold text-[#2BC7B7] mb-2" data-id="element-417">
                      Message Sent
                    </motion.h2>
                    <motion.p initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.3
                }} className="text-[#F3F5F1]/80 mb-8" data-id="element-418">
                      Thank you for reaching out. We'll be in touch soon.
                    </motion.p>
                    <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.4
                }} data-id="element-419">
                      <Button variant="outline" onClick={resetForm} data-id="element-420">
                        Send Another
                      </Button>
                    </motion.div>
                  </motion.div>}
              </AnimatePresence>

              {/* ROOT VISUALIZATION LAYER (Behind inputs) */}
              <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden" data-id="element-421">
                <svg className="w-full h-full" preserveAspectRatio="none" data-id="element-422">
                  <defs data-id="element-423">
                    <filter id="glow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-424">
                      <feGaussianBlur stdDeviation="4" result="blur" data-id="element-425" />
                      <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-426" />
                    </filter>
                    <linearGradient id="rootGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-427">
                      <stop offset="0%" stopColor="#2BC7B7" data-id="element-428" />
                      <stop offset="100%" stopColor="#1F3A33" stopOpacity="0" data-id="element-429" />
                    </linearGradient>
                  </defs>

                  {/* Name Root */}
                  <g transform="translate(100, 100)" data-id="element-430">
                    {' '}
                    {/* Approximate position below Name input */}
                    {/* We need dynamic positioning. Since we can't easily get exact DOM rects in this static SVG setup without refs,
                     we'll use percentage based positioning that aligns with the inputs.
                     Name Input is roughly at top 15%. Email at 40%. Message at 65%.
                     */}
                  </g>
                </svg>

                {/* Dynamic SVG Layer using absolute positioning relative to container */}
                {/* Name Root */}
                <div className="absolute top-[100px] left-1/2 -translate-x-1/2 w-full h-[500px] pointer-events-none" data-id="element-431">
                  <svg className="w-full h-full overflow-visible" data-id="element-432">
                    <RootSystem isActive={form.name.length > 0} isValid={isNameValid} length={nameRootLength} seed={1} color={isNameValid ? COLORS.glowTeal : COLORS.teal} opacity={isNameValid ? 1 : 0.6} data-id="element-433" />
                  </svg>
                </div>

                {/* Email Root */}
                <div className="absolute top-[200px] left-1/2 -translate-x-1/2 w-full h-[400px] pointer-events-none" data-id="element-434">
                  <svg className="w-full h-full overflow-visible" data-id="element-435">
                    <RootSystem isActive={form.email.length > 0} isValid={isEmailValid} length={emailRootLength} seed={2} color={isEmailValid ? COLORS.glowTeal : COLORS.teal} opacity={isEmailValid ? 1 : 0.6} data-id="element-436" />
                  </svg>
                </div>

                {/* Message Root */}
                <div className="absolute top-[380px] left-1/2 -translate-x-1/2 w-full h-[250px] pointer-events-none" data-id="element-437">
                  <svg className="w-full h-full overflow-visible" data-id="element-438">
                    <RootSystem isActive={form.message.length > 0} isValid={isMessageValid} length={messageRootLength} seed={3} color={isMessageValid ? COLORS.glowTeal : COLORS.teal} opacity={isMessageValid ? 1 : 0.6} data-id="element-439" />
                  </svg>
                </div>

                {/* Convergence Zone Bloom (Only when all valid) */}
                <div className="absolute bottom-0 left-0 w-full h-[200px] flex items-end justify-center pb-20" data-id="element-440">
                  <AnimatePresence data-id="element-441">
                    {allValid && <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="absolute bottom-[130px]" data-id="element-442">
                        <div className="w-4 h-4 rounded-full bg-[#4DE7D4] shadow-[0_0_20px_#4DE7D4] animate-pulse" data-id="element-443" />
                        {/* Connecting tendrils */}
                        <svg className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[300px] h-[200px] overflow-visible" style={{
                      transform: 'translateX(-50%)'
                    }} data-id="element-444">
                          <motion.path d="M 0 0 Q -50 -50, -100 -150" stroke="#4DE7D4" strokeWidth="1" fill="none" initial={{
                        pathLength: 0
                      }} animate={{
                        pathLength: 1
                      }} transition={{
                        duration: 1
                      }} data-id="element-445" />
                          <motion.path d="M 0 0 Q 50 -50, 100 -150" stroke="#4DE7D4" strokeWidth="1" fill="none" initial={{
                        pathLength: 0
                      }} animate={{
                        pathLength: 1
                      }} transition={{
                        duration: 1
                      }} data-id="element-446" />
                          <motion.path d="M 0 0 L 0 -100" stroke="#4DE7D4" strokeWidth="1" fill="none" initial={{
                        pathLength: 0
                      }} animate={{
                        pathLength: 1
                      }} transition={{
                        duration: 1
                      }} data-id="element-447" />
                        </svg>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Bloom Animation on Success */}
                  <AnimatePresence data-id="element-448">
                    {status === 'success' && <motion.div className="absolute bottom-[130px] z-10" data-id="element-449">
                        {/* Radial explosion */}
                        {[...Array(12)].map((_, i) => <motion.div key={i} className="absolute top-0 left-0 w-1 h-40 bg-gradient-to-t from-transparent to-[#4DE7D4]" style={{
                      transformOrigin: 'bottom center',
                      rotate: i * 30
                    }} initial={{
                      scaleY: 0,
                      opacity: 0
                    }} animate={{
                      scaleY: 1,
                      opacity: [0, 1, 0]
                    }} transition={{
                      duration: 1.5,
                      delay: 0.5
                    }} data-id="element-450" />)}
                        <motion.div className="w-[600px] h-[600px] rounded-full bg-radial-gradient from-[#2BC7B7]/20 to-transparent absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" initial={{
                      scale: 0,
                      opacity: 0
                    }} animate={{
                      scale: 1,
                      opacity: [0, 0.5, 0]
                    }} transition={{
                      duration: 2
                    }} data-id="element-451" />
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              {/* FORM CONTENT (Relative z-10) */}
              <form onSubmit={handleSubmit} className="relative z-10 space-y-8" data-id="element-452">
                {/* Field 1: Name */}
                <div className="relative group" data-id="element-453">
                  <label className="block text-xs font-medium text-[#F3F5F1]/70 uppercase tracking-[2px] mb-2 font-inter" data-id="element-454">
                    Your Name
                  </label>
                  <div className="relative" data-id="element-455">
                    <input type="text" value={form.name} onChange={e => setForm({
                    ...form,
                    name: e.target.value
                  })} onFocus={() => setFocused('name')} onBlur={() => setFocused(null)} placeholder="Enter your name" className={`w-full bg-[#1F3A33] border-2 rounded-xl px-5 py-4 text-[#F3F5F1] placeholder-[#F3F5F1]/40 outline-none transition-all duration-300
                        ${focused === 'name' ? 'border-[#2BC7B7] shadow-[0_0_20px_rgba(43,199,183,0.3)]' : 'border-transparent'}
                        ${isNameValid ? 'border-[#2BC7B7]/50' : ''}
                      `} data-id="element-456" />
                    {isNameValid && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2BC7B7]" data-id="element-457">
                        <Check className="w-5 h-5" data-id="element-458" />
                      </div>}
                  </div>
                </div>

                {/* Field 2: Email */}
                <div className="relative group pt-12" data-id="element-459">
                  {' '}
                  {/* Extra padding for root space */}
                  <label className="block text-xs font-medium text-[#F3F5F1]/70 uppercase tracking-[2px] mb-2 font-inter" data-id="element-460">
                    Email Address
                  </label>
                  <div className="relative" data-id="element-461">
                    <input type="email" value={form.email} onChange={e => setForm({
                    ...form,
                    email: e.target.value
                  })} onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} placeholder="your@email.com" className={`w-full bg-[#1F3A33] border-2 rounded-xl px-5 py-4 text-[#F3F5F1] placeholder-[#F3F5F1]/40 outline-none transition-all duration-300
                        ${focused === 'email' ? 'border-[#2BC7B7] shadow-[0_0_20px_rgba(43,199,183,0.3)]' : 'border-transparent'}
                        ${isEmailValid ? 'border-[#2BC7B7]/50' : ''}
                        ${form.email.length > 0 && !isEmailValid && focused !== 'email' ? 'border-[#A38E78]' : ''}
                      `} data-id="element-462" />
                    {isEmailValid && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2BC7B7]" data-id="element-463">
                        <Check className="w-5 h-5" data-id="element-464" />
                      </div>}
                    {form.email.length > 0 && !isEmailValid && focused !== 'email' && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A38E78]" data-id="element-465">
                          <AlertCircle className="w-5 h-5" data-id="element-466" />
                        </div>}
                  </div>
                </div>

                {/* Field 3: Message */}
                <div className="relative group pt-12" data-id="element-467">
                  <label className="block text-xs font-medium text-[#F3F5F1]/70 uppercase tracking-[2px] mb-2 font-inter" data-id="element-468">
                    Your Message
                  </label>
                  <div className="relative" data-id="element-469">
                    <textarea value={form.message} onChange={e => setForm({
                    ...form,
                    message: e.target.value
                  })} onFocus={() => setFocused('message')} onBlur={() => setFocused(null)} placeholder="Tell us about your project, question, or how you'd like to collaborate..." rows={5} className={`w-full bg-[#1F3A33] border-2 rounded-xl px-5 py-4 text-[#F3F5F1] placeholder-[#F3F5F1]/40 outline-none transition-all duration-300 resize-y min-h-[160px]
                        ${focused === 'message' ? 'border-[#2BC7B7] shadow-[0_0_20px_rgba(43,199,183,0.3)]' : 'border-transparent'}
                        ${isMessageValid ? 'border-[#2BC7B7]/50' : ''}
                      `} data-id="element-470" />
                  </div>
                </div>

                {/* Convergence Zone Text */}
                <div className="h-24 flex items-center justify-center" data-id="element-471">
                  <motion.p className={`text-sm tracking-widest uppercase font-medium transition-colors duration-500 ${allValid ? 'text-[#4DE7D4]' : 'text-[#F3F5F1]/20'}`} animate={{
                  opacity: allValid ? 1 : 0.4
                }} data-id="element-472">
                    {allValid ? 'Ready to grow' : 'Roots seeking connection...'}
                  </motion.p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pb-4" data-id="element-473">
                  <button type="submit" disabled={!allValid || status !== 'idle'} className={`
                      relative px-12 py-4 rounded-full border-2 text-sm font-semibold tracking-[2px] uppercase transition-all duration-300
                      ${!allValid ? 'border-[#2BC7B7]/30 text-[#2BC7B7]/40 cursor-not-allowed' : 'border-[#2BC7B7] text-[#2BC7B7] hover:bg-[#2BC7B7] hover:text-[#0D2B24] hover:shadow-[0_0_30px_rgba(43,199,183,0.5)] hover:scale-105 active:scale-95'}
                      ${status === 'sending' ? 'bg-[#2BC7B7] text-[#0D2B24] shadow-[0_0_40px_rgba(43,199,183,0.6)]' : ''}
                    `} data-id="element-474">
                    {status === 'sending' ? 'SENDING...' : 'SEND MESSAGE'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>

          {/* Right Column: Founder & Info */}
          <div className="lg:col-span-4 lg:col-start-11 xl:col-span-3 xl:col-start-10 space-y-8 lg:pt-20" data-id="element-475">
            {/* Founder Card */}
            <div className="hidden lg:block" data-id="element-476">
              <FounderCard data-id="element-477" />
            </div>

            {/* Mobile/Tablet Founder Card (shown below form usually, but here handling responsive grid) */}
          </div>
        </div>

        {/* Bottom Section: Alternative Methods */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-478">
          <ContactCard icon={Mail} title="Email Us Directly" value="natalya@vitalityvf.com" href="mailto:natalya@vitalityvf.com" data-id="element-479" />
          <ContactCard icon={Linkedin} title="Connect on LinkedIn" value="Vitality Venture Fund" href="https://www.linkedin.com/company/vitality-vf/" data-id="element-480" />
          <ContactCard icon={MapPin} title="Based In" value="United States" href={null} data-id="element-481" />
        </div>

        {/* Mobile Founder Card (visible only on small screens) */}
        <div className="mt-12 lg:hidden flex justify-center" data-id="element-482">
          <FounderCard data-id="element-483" />
        </div>
      </main>
    </div>;
}
// --- Sub-component: Root System Visualizer ---
const RootSystem = ({
  isActive,
  isValid,
  length,
  seed,
  color,
  opacity
}: {
  isActive: boolean;
  isValid: boolean;
  length: number;
  seed: number;
  color: string;
  opacity: number;
}) => {
  // Generate main path
  const mainPath = useMemo(() => generateWiggle(0, 0, 300, seed), [seed]);
  // Generate branches based on length
  const branches = useMemo(() => {
    const count = Math.floor(length / 50);
    return generateBranches(mainPath, count, seed);
  }, [length, seed, mainPath]);
  return <g style={{
    opacity,
    transition: 'opacity 0.5s ease'
  }} data-id="element-484">
      {/* Glow Filter */}
      <defs data-id="element-485">
        <filter id={`glow-${seed}`} data-id="element-486">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" data-id="element-487" />
          <feMerge data-id="element-488">
            <feMergeNode in="coloredBlur" data-id="element-489" />
            <feMergeNode in="SourceGraphic" data-id="element-490" />
          </feMerge>
        </filter>
      </defs>

      {/* Main Root */}
      <motion.path d={mainPath} stroke={color} strokeWidth={isValid ? 3 : 2} fill="none" strokeLinecap="round" filter={`url(#glow-${seed})`} initial={{
      pathLength: 0
    }} animate={{
      pathLength: isActive ? length / 300 : 0
    }} // Normalize length to path
    transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} data-id="element-491" />

      {/* Branches */}
      {branches.map((branch, i) => <motion.path key={branch.id} d={branch.d} stroke={color} strokeWidth={1.5} fill="none" strokeLinecap="round" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 0.8,
      delay: 0.2 + i * 0.1
    }} transform={`translate(0, ${branch.yOffset})`} // Simplified positioning
    data-id="element-492" />)}

      {/* Pulse Effect for Valid Roots */}
      {isValid && <motion.circle r="2" fill="#fff" initial={{
      offsetDistance: '0%'
    }} animate={{
      offsetDistance: '100%'
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'linear',
      repeatDelay: 1
    }} style={{
      offsetPath: `path("${mainPath}")`
    }} data-id="element-493" />}
    </g>;
};